class KAtpNavigation extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.header = this.querySelector('[data-k-atp-header]');
    this.desktopMenu = this.querySelector('[data-desktop-menu]');
    this.mobileDrawer = this.querySelector('[data-mobile-drawer]');
    this.mobileOpen = this.querySelector('[data-mobile-menu-open]');
    this.addEventListener('click', this.onClick, options);
    document.addEventListener('click', (e) => { if (!e.target.closest?.('[data-nav-item]')) this.querySelectorAll('[data-menu-toggle][aria-expanded="true"]').forEach(b => b.setAttribute('aria-expanded', 'false')); }, options);
    this.querySelectorAll('[data-nav-item]').forEach(item => item.addEventListener('pointerenter', () => this.querySelectorAll('[data-nav-item]').forEach(o => { if (o !== item) o.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false'); }), options));
    document.addEventListener('keydown', this.onKeyDown, options);
    document.addEventListener('change', this.onLocalization, options);
    document.addEventListener('shopify:cart:lines-update', this.onCartUpdate, options);
    window.addEventListener('pageshow', () => this.closeAll(false), options);
    window.matchMedia('(max-width: 989px)').addEventListener('change', () => this.closeAll(false), options);
    this.observer = new ResizeObserver(() => this.syncLayout());
    this.observer.observe(this);
    this.syncLayout();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    document.body.classList.remove('k-atp-menu-locked');
  }

  syncLayout() {
    const height = Math.ceil(this.getBoundingClientRect().height);
    const headerHeight = Math.ceil(this.header?.getBoundingClientRect().height || 68);
    document.body.style.setProperty('--header-height', `${headerHeight}px`);
    document.body.style.setProperty('--header-group-height', `${height}px`);
    document.body.style.setProperty('--top-row-height', `${headerHeight}px`);
    this.style.setProperty('--k-atp-menu-top', `${Math.max(0, this.header?.getBoundingClientRect().bottom || height)}px`);
  }

  closeAll(restoreFocus = true) {
    const open = this.querySelector('[data-menu-toggle][aria-expanded="true"]');
    const mobileWasOpen = this.mobileDrawer.classList.contains('is-open');
    if (this.desktopMenu) this.desktopMenu.hidden = true;
    this.header.classList.remove('is-menu-open');
    this.querySelectorAll('[data-menu-toggle]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    this.mobileDrawer.classList.remove('is-open');
    this.mobileDrawer.setAttribute('aria-hidden', 'true');
    this.mobileDrawer.inert = true;
    this.mobileOpen.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('k-atp-menu-locked');
    if (restoreFocus) (mobileWasOpen ? this.mobileOpen : open)?.focus();
  }

  toggleDesktop(button) {
    // step40: small dropdown under the item (CSS :hover / :focus-within / aria-expanded); no overlay, no scroll lock
    const wasOpen = button.getAttribute('aria-expanded') === 'true';
    this.querySelectorAll('[data-menu-toggle]').forEach(b => b.setAttribute('aria-expanded', 'false'));
    if (!wasOpen) button.setAttribute('aria-expanded', 'true');
  }

  openMobile() {
    this.closeAll(false);
    this.mobileDrawer.inert = false;
    this.mobileDrawer.classList.add('is-open');
    this.mobileDrawer.setAttribute('aria-hidden', 'false');
    this.mobileOpen.setAttribute('aria-expanded', 'true');
    document.body.classList.add('k-atp-menu-locked');
    this.mobileDrawer.querySelector('[data-mobile-menu-close]').focus();
  }

  onClick = (event) => {
    const button = event.target.closest('button');
    if (!button) return;
    if (button.matches('[data-menu-toggle]')) this.toggleDesktop(button);
    else if (button.matches('[data-mobile-menu-open]')) this.openMobile();
    else if (button.matches('[data-menu-close], [data-mobile-menu-close]')) this.closeAll();
    else if (button.matches('[data-mobile-submenu-toggle]')) {
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      const icon = button.querySelector('span');
      if (icon && !icon.hasAttribute('data-chevron')) icon.textContent = open ? '\u2212' : '+';
      const sub = button.closest('li')?.querySelector(':scope > ul') || button.nextElementSibling;
      if (sub) sub.hidden = !open;
    }
  };

  onKeyDown = (event) => {
    if (event.key === 'Escape') this.closeAll();
    if (event.key !== 'Tab' || !this.mobileDrawer.classList.contains('is-open')) return;
    const focusable = [...this.mobileDrawer.querySelectorAll('a[href],button')]
      .filter(element => element.getClientRects().length > 0);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  onLocalization = (event) => {
    if (event.target.matches('[data-k-atp-localization]')) event.target.form?.requestSubmit();
  };

  onCartUpdate = (event) => {
    event.promise?.then(({ cart, detail }) => {
      if (!this.isConnected) return;
      const count = cart?.totalQuantity ?? detail?.itemCount;
      if (!Number.isInteger(count) || count < 0) return;
      this.querySelectorAll('[data-cart-count]').forEach(element => {
        element.textContent = String(count);
        if (element.classList.contains('k-atp-cart-count')) element.hidden = count === 0;
      });
    }).catch(error => {
      if (error?.name !== 'AbortError') console.warn('[KASSANDRA navigation] Cart update failed', error);
    });
  };
}

if (!customElements.get('k-atp-navigation')) customElements.define('k-atp-navigation', KAtpNavigation);


/* KASSANDRA: header "Carrello" / cart icon opens the cart drawer (href stays /cart as no-JS fallback) */
if (!window.__kcHeaderCartDrawer) {
  window.__kcHeaderCartDrawer = true;
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('.k-atp-cart-link, .k-atp-mobile-cart') : null;
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const drawer = document.getElementById('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    if (!drawer.isOpen) drawer.open();
  });
  const markCartLinks = () => {
    if (!document.getElementById('cart-drawer')) return;
    document.querySelectorAll('.k-atp-cart-link, .k-atp-mobile-cart').forEach((link) => {
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', 'cart-drawer');
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', markCartLinks);
  else markCartLinks();
}
